// @ts-nocheck
import { DIMENSION_OPTION, OPTION_TYPE_EGRESS_COMPLAINT, OPTION_TYPE_SASH, OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE } from "../constants";
import { IAssistantMessage } from "../types";

// ─── Option type constants ────────────────────────────────────────────────────
export const OPTION_TYPE_TRANSOM_DROP = 5;
export const OPTION_TYPE_CUSTOM_FRACTION = 59;
export const OPTION_TYPE_NUMERIC_VALUES = 58;

// Sash operation answers (OPTION_TYPE_SASH = 7). A fixed sash cannot open, so
// it can never provide fire egress regardless of its geometry.
export type SashOperationValue =
  | "both_sashes_fixed"
  | "both_sashes_slide"
  | "bottom_sash_fixed"
  | "top_sash_fixed";

/** Sashes where neither side can move enough to provide egress. */
export const NON_OPERABLE_SASH_VALUES = new Set<string>([
  "both_sashes_fixed",
  "bottom_sash_fixed",
]);

export function getSashResponseValue(
  messages: IAssistantMessage[],
): string | undefined {
  for (const m of messages) {
    if (m.question?.optionType === OPTION_TYPE_SASH && m.response?.answer?.length > 0) {
      const a = m.response.answer[0];
      return a.responseValue ?? a.optionText;
    }
  }
  return undefined;
}

// ─── Spreadsheet-verified constants ──────────────────────────────────────────
const MIN_DIMENSION_MM = 450;
const MIN_AREA_M2 = 0.33;

const DEDUCTIONS = {
  width: 130,
  // 89mm frame deduction + 100mm top stop profile (factory fixed) = 189mm
  bottomSashSliding: 189,
  bottomSashTilting: 76,
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────
export type FireEgressWindowType =
  | "arch"
  | "standard"
  | "bay"
  | "couple"
  | "unknown";

/** Independent sliding/tilting result: 'both' when each sash meets egress on its own. */
export type FireEgressPass = "sliding" | "tilting" | "both" | "none";

export interface FireEgressInput {
  windowType: FireEgressWindowType;
  windowWidth: number; // mm
  windowHeight: number; // mm
  topSash?: number; // mm — top sash height. Defaults to windowHeight / 2
  bottomSash?: number; // mm — bottom sash height. Defaults to windowHeight / 2
  /** "Both Sashes Fixed" / "Bottom Sash Fixed" / etc — gates egress regardless of geometry. */
  sashOperation?: SashOperationValue;
}

export type FireEgressResult =
  | { applicable: false; reason: string }
  | {
      applicable: true;
      pass: FireEgressPass;
      widthCO: number;
      slidingBottomCO: number;
      tiltingBottomCO: number;
      slidingArea: number;
      tiltingArea: number;
    };

export interface ExtractedDimensions {
  windowWidth?: number;
  frameWidths?: number[];
  windowHeight?: number;
  topSash?: number;
  bottomSash?: number;
  /** Single-frame / "same for all" sash operation answer. */
  sashOperation?: SashOperationValue;
  /** Per-frame sash operation, aligned by index to frameWidths (Individual Configuration). */
  frameSashOperations?: (SashOperationValue | undefined)[];
}

export interface FrameEgressResult {
  frameIndex: number;
  frameWidth: number;
  result: FireEgressResult;
}

// ─── Multi-frame helper (bay / couple) ───────────────────────────────────────

export function calculateFireEgressMultiFrame(
  frameWidths: number[],
  windowType: FireEgressWindowType,
  windowHeight: number,
  topSash?: number,
  bottomSash?: number,
  frameSashOperations?: (SashOperationValue | undefined)[],
): { frames: FrameEgressResult[]; anyCompliant: boolean } {
  const frames: FrameEgressResult[] = frameWidths.map((fw, i) => ({
    frameIndex: i + 1,
    frameWidth: fw,
    result: calculateFireEgress({
      windowType,
      windowWidth: fw,
      windowHeight,
      topSash,
      bottomSash,
      sashOperation: frameSashOperations?.[i],
    }),
  }));

  const anyCompliant = frames.some(
    (f) => f.result.applicable && f.result.pass !== "none",
  );

  return { frames, anyCompliant };
}

// ─── Main calculation ─────────────────────────────────────────────────────────

/**
 * Fire egress check. Sliding and tilting are each evaluated independently:
 * sliding compliant when widthCO ≥ 450mm AND (widthCO × slidingBottomCO) ≥ 0.33m²,
 * tilting compliant when widthCO ≥ 450mm AND (widthCO × tiltingBottomCO) ≥ 0.33m².
 * Returns 'both' when both pass, otherwise 'sliding'/'tilting'/'none'.
 * A fixed (non-operable) sash short-circuits to pass 'none' regardless of geometry.
 */
export function calculateFireEgress(input: FireEgressInput): FireEgressResult {
  const { windowType, windowWidth, windowHeight, sashOperation } = input;

  if (windowType === "arch") {
    return {
      applicable: false,
      reason:
        "Fire egress cannot be calculated for arched or swept head windows.",
    };
  }

  // A fixed (non-operable) sash can never provide fire egress, no matter how
  // large the frame is — skip the geometry check entirely.
  if (sashOperation && NON_OPERABLE_SASH_VALUES.has(sashOperation)) {
    const topSash = input.topSash ?? windowHeight / 2;
    const bottomSash = input.bottomSash ?? windowHeight / 2;
    const widthCO = windowWidth - DEDUCTIONS.width;
    return {
      applicable: true,
      pass: "none",
      widthCO,
      slidingBottomCO: topSash - DEDUCTIONS.bottomSashSliding,
      tiltingBottomCO: bottomSash - DEDUCTIONS.bottomSashTilting,
      slidingArea: 0,
      tiltingArea: 0,
    };
  }

  const topSash = input.topSash ?? windowHeight / 2;
  const bottomSash = input.bottomSash ?? windowHeight / 2;

  const widthCO = windowWidth - DEDUCTIONS.width;
  const slidingBottomCO = topSash - DEDUCTIONS.bottomSashSliding;
  const tiltingBottomCO = bottomSash - DEDUCTIONS.bottomSashTilting;

  const slidingArea = (widthCO * slidingBottomCO) / 1_000_000;
  const tiltingArea = (widthCO * tiltingBottomCO) / 1_000_000;

  const base = {
    applicable: true as const,
    widthCO,
    slidingBottomCO,
    tiltingBottomCO,
    slidingArea,
    tiltingArea,
  };

  // Sliding and tilting evaluated independently.
  const slidingCompliant = widthCO >= MIN_DIMENSION_MM && slidingArea >= MIN_AREA_M2;
  const tiltingCompliant = widthCO >= MIN_DIMENSION_MM && tiltingArea >= MIN_AREA_M2;

  if (slidingCompliant && tiltingCompliant) {
    return { ...base, pass: "both" };
  }
  if (slidingCompliant) {
    return { ...base, pass: "sliding" };
  }
  if (tiltingCompliant) {
    return { ...base, pass: "tilting" };
  }

  return { ...base, pass: "none" };
}

// ─── Message extraction helpers ───────────────────────────────────────────────

const FRAME_WIDTH_KEYS = [
  "window_1_width_(mm)",
  "window_2_width_(mm)",
  "window_3_width_(mm)",
  "window_4_width_(mm)",
  "window_5_width_(mm)",
];

export function extractDimensionsFromMessages(
  messages: IAssistantMessage[],
): ExtractedDimensions {
  const result: ExtractedDimensions = {};

  for (const msg of messages) {
    if (!msg.response) continue;

    // Overall frame dimensions
    if (msg.question?.optionType === DIMENSION_OPTION && msg.response.form) {
      const form = msg.response.form;
      const height = form["overall_height_(mm)"];
      if (height !== undefined) result.windowHeight = Number(height);

      const frameWidths = FRAME_WIDTH_KEYS.map((key) =>
        form[key] !== undefined ? Number(form[key]) : null,
      ).filter((v): v is number => v !== null);

      if (frameWidths.length > 0) {
        result.frameWidths = frameWidths;
      } else {
        const width = form["width_(mm)"] ?? form["overall_width_(mm)"];
        if (width !== undefined) result.windowWidth = Number(width);
      }
    }

    // Explicit sash heights from the numeric-values question
    if (
      msg.question?.optionType === OPTION_TYPE_NUMERIC_VALUES &&
      msg.response.form
    ) {
      const form = msg.response.form;
      const opts = msg.question.options ?? [];
      let topSash: number | undefined;
      let bottomSash: number | undefined;

      if (form["top_sash"] !== undefined) topSash = Number(form["top_sash"]);
      if (form["bottom_sash"] !== undefined)
        bottomSash = Number(form["bottom_sash"]);

      if (topSash === undefined || bottomSash === undefined) {
        for (const opt of opts) {
          const key = opt.responseValue || opt.optionId;
          const val = form[key];
          if (val === undefined || val === "") continue;
          const text = (opt.optionText ?? "").toLowerCase();
          if (topSash === undefined && text.includes("top"))
            topSash = Number(val);
          else if (bottomSash === undefined && text.includes("bottom"))
            bottomSash = Number(val);
        }
      }

      if (topSash === undefined || bottomSash === undefined) {
        const filled = opts.filter((o) => {
          const k = o.responseValue || o.optionId;
          return form[k] !== undefined && form[k] !== "";
        });
        if (filled.length >= 2) {
          if (topSash === undefined)
            topSash = Number(
              form[filled[0].responseValue || filled[0].optionId],
            );
          if (bottomSash === undefined)
            bottomSash = Number(
              form[filled[1].responseValue || filled[1].optionId],
            );
        }
      }

      if (topSash !== undefined) result.topSash = topSash;
      if (bottomSash !== undefined) result.bottomSash = bottomSash;
    }

    // Custom fraction: derive sash heights as % of window height
    if (
      msg.question?.optionType === OPTION_TYPE_CUSTOM_FRACTION &&
      msg.response.form
    ) {
      const form = msg.response.form;
      const topPct = form["top_percentage"];
      const bottomPct = form["bottom_percentage"];
      if (
        topPct !== undefined &&
        bottomPct !== undefined &&
        result.windowHeight
      ) {
        result.topSash = result.windowHeight * (Number(topPct) / 100);
        result.bottomSash = result.windowHeight * (Number(bottomPct) / 100);
      }
    }

    // Sash operation (Both Sashes Slide / Fixed, Bottom/Top Sash Fixed) —
    // per-frame for Individual Configuration (responsePath ends "frameN"),
    // or a single "same for all" answer otherwise.
    if (msg.question?.optionType === OPTION_TYPE_SASH && msg.response.answer?.length) {
      const value = (msg.response.answer[0]?.responseValue ?? "") as SashOperationValue;
      if (value) {
        const responsePath = msg.question.responsePath ?? "";
        const frameMatch = responsePath.match(/frame(\d+)$/i);
        if (frameMatch) {
          const idx = Number(frameMatch[1]) - 1;
          if (!result.frameSashOperations) result.frameSashOperations = [];
          result.frameSashOperations[idx] = value;
        } else {
          result.sashOperation = value;
        }
      }
    }
  }

  // Backfill per-frame sash operation from the "same for all" answer for any
  // frame that didn't get an individual one.
  if (result.frameWidths && result.frameWidths.length > 0 && result.sashOperation) {
    if (!result.frameSashOperations) result.frameSashOperations = [];
    result.frameWidths.forEach((_, i) => {
      if (result.frameSashOperations![i] === undefined) {
        result.frameSashOperations![i] = result.sashOperation;
      }
    });
  }

  return result;
}

export function resolveSashHeights(
  transomResponseValue: string,
  windowHeight: number,
  topSash?: number,
  bottomSash?: number,
): { topSash: number; bottomSash: number } {
  if (topSash !== undefined && bottomSash !== undefined) {
    return { topSash, bottomSash };
  }
  return { topSash: windowHeight / 2, bottomSash: windowHeight / 2 };
}

export function extractWindowTypeFromMessages(messages: IAssistantMessage[]): FireEgressWindowType {
  for (const msg of messages) {
    if (
      (msg.question?.optionType === OPTION_TYPE_VARIATION || msg.question?.optionType === OPTION_TYPE_WINDOW_TYPE) &&
      msg.response?.answer?.length > 0
    ) {
      const ptc = msg.response.answer.find((a) => a.producttypecode != null)?.producttypecode;
      if (ptc !== undefined) {
        if (ptc === 2) return 'arch';
        if (ptc === 4) return 'couple';
        if (ptc === 3) return 'bay';
        return 'standard';
      }
      const text = (msg.response.answer[0]?.optionText ?? '').toLowerCase();
      if (text.includes('arch')) return 'arch';
      if (text.includes('bay')) return 'bay';
      if (text.includes('couple') || text.includes('coupled')) return 'couple';
      return 'standard';
    }
  }
  return 'unknown';
}

export type FireEgressBadgeStatus =
  | null
  | { compliant: true; pass: 'sliding' | 'tilting' | 'both' }
  | { compliant: false };

/**
 * Resolve fire egress badge status from chatbot messages.
 * Returns null when egress was not answered Yes, or when not applicable (arch).
 */
export function resolveFireEgressBadge(messages: IAssistantMessage[]): FireEgressBadgeStatus {
  // 1. Must have egress answered Yes (guard against bot messages with no response)
  const egressMsg = messages.find(
    (m) => m.question?.optionType === OPTION_TYPE_EGRESS_COMPLAINT && m.response?.answer?.length > 0,
  );
  if (!egressMsg) return null;
  const answer = egressMsg.response.answer[0];
  const isYes = (answer.optionText ?? answer.responseValue ?? '').toLowerCase().includes('yes');
  if (!isYes) return null;

  // 2. Dimensions
  const dims = extractDimensionsFromMessages(messages);
  if (!dims.windowHeight) return { compliant: false };

  // 3. Window type
  const windowType = extractWindowTypeFromMessages(messages);
  if (windowType === 'arch') return null;

  // 4. Sash heights
  const sashes = resolveSashHeights('custom', dims.windowHeight, dims.topSash, dims.bottomSash);

  // 5. Multi-frame (bay / couple)
  if (dims.frameWidths && dims.frameWidths.length > 1) {
    const multi = calculateFireEgressMultiFrame(
      dims.frameWidths,
      windowType,
      dims.windowHeight,
      sashes.topSash,
      sashes.bottomSash,
      dims.frameSashOperations,
    );
    if (!multi.anyCompliant) return { compliant: false };
    const passing = multi.frames.find((f) => f.result.applicable && f.result.pass !== 'none');
    if (!passing || !passing.result.applicable) return { compliant: false };
    const pass: 'sliding' | 'tilting' | 'both' =
      passing.result.pass === 'both' ? 'both'
        : passing.result.pass === 'tilting' ? 'tilting'
          : 'sliding';
    return { compliant: true, pass };
  }

  // 6. Single frame
  const w = dims.windowWidth ?? dims.frameWidths?.[0];
  if (!w) return { compliant: false };

  const result = calculateFireEgress({
    windowType,
    windowWidth: w,
    windowHeight: dims.windowHeight,
    topSash: sashes.topSash,
    bottomSash: sashes.bottomSash,
    sashOperation: dims.sashOperation,
  });
  if (!result.applicable || result.pass === 'none') return { compliant: false };
  const pass: 'sliding' | 'tilting' | 'both' =
    result.pass === 'both' ? 'both' : result.pass === 'tilting' ? 'tilting' : 'sliding';
  return { compliant: true, pass };
}
